import axios from '../config/axios';
const API_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api/documentEditor`;
const documentEditorService = {
  // Protocol Section Service Methods
  createSection: async (sectionData, projectId) => {
    try {
      console.log('Creating new section:', sectionData.title, 'for project:', projectId);
      
      const response = await axios.post(`${API_URL}/create`, {
        ...sectionData,
        projectId
      });

      if (response.data && response.data.document) {
        console.log('Successfully created section:', response.data.document._id);
        return response.data;
      }
      
      throw new Error('Failed to create section');
    } catch (error) {
      console.error('Error creating section:', error.response ? error.response.data : error.message);
      throw error.response?.data || error.message;
    }
  },

  // Get ALL Protocol Sections Available in the Database
  getAllSections: async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/all`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  //Update Protocol Section from database
  updateSection: async (sectionId, updateData, projectId) => {
    try {
      const response = await axios.put(`${API_URL}/update/${sectionId}`, { ...updateData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  //Delete Protocol Section from database
  deleteSection: async (sectionId, projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/delete/${sectionId}`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  //Delete All Protocol Sections from database
  deleteAllSections: async (projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/deleteAll`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  //AddNew Version of the Protocol Document (Sections)
  addNewVersion: async (versionData, projectId) => {
    try {
      const response = await axios.post(`${API_URL}/createVersion`, { ...versionData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get ALL Versions Available in the Database for Protocol Sections
  getAllVersions: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/allVersions/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getSectionByTitle: async (title, projectId, documentType = 'PROTOCOL', silent = false) => {
    try {
      if (!silent) {
        console.log('Fetching section by title:', title, 'for project:', projectId, 'documentType:', documentType);
      }
      
      // Check if this is a subsection (expected to not exist as separate section)
      const isSubsection = /^\d+\.\d+/.test(title);
      
      // Try multiple title variations to match different storage formats
      const titleVariations = [
        title.trim(), // Original title
        title.trim().toLowerCase(), // Lowercase
        title.trim().replace(/\s+/g, ' '), // Normalized spaces
        title.trim().replace(/\s+/g, ' ').toLowerCase(), // Normalized + lowercase
        title.trim().replace(/[^\w\s.-]/g, '').toLowerCase(), // Remove special chars + lowercase (current method)
        title.trim().replace(/\./g, '. ').trim(), // Add space after dots
        title.trim().replace(/\.\s*/g, '.'), // Remove spaces after dots
      ];
      
      // Remove duplicates
      const uniqueVariations = [...new Set(titleVariations)];
      
      // Try each variation until one works
      for (const titleToTry of uniqueVariations) {
        try {
          const response = await axios.get(`${API_URL}/sections/byTitleAndProject`, {
            params: { 
              title: titleToTry, // Let axios URL-encode params
              projectId, 
              documentType 
            },
            // For subsections, don't throw error on 404 (expected behavior)
            validateStatus: (status) => {
              if (isSubsection && status === 404) {
                return true; // Don't throw error for 404 on subsections
              }
              return status >= 200 && status < 300; // Normal validation for other cases
            }
          });

          // If 404 for subsection, try next variation
          if (isSubsection && response.status === 404) {
            continue;
          }

          if (response.data && response.data._id) {
            if (!silent) {
              console.log('Found section:', response.data._id, 'using title variation:', titleToTry);
            }
            return response.data;
          }
        } catch (error) {
          // If not a 404, or if it's a main section, throw the error
          if (!isSubsection || error.response?.status !== 404) {
            throw error;
          }
          // For subsection 404s, continue to next variation
          continue;
        }
      }
      
      // If all variations failed and it's a subsection, return null (expected)
      if (isSubsection) {
        return null;
      }
      
      return null;
    } catch (error) {
      // For subsections (which often don't exist as separate sections), don't log errors
      // The caller will handle the fallback to parent section extraction
      const isSubsection = /^\d+\.\d+/.test(title);
      
      // Handle error object properly
      let errorToThrow;
      if (error.response?.data) {
        // If it's an error response object, check if it has a message or use the whole object
        if (typeof error.response.data === 'object' && error.response.data.message) {
          errorToThrow = error.response.data;
        } else if (typeof error.response.data === 'string') {
          errorToThrow = { message: error.response.data };
        } else {
          errorToThrow = error.response.data;
        }
      } else if (error.message) {
        errorToThrow = { message: error.message };
      } else {
        errorToThrow = { message: String(error) };
      }
      
      if (!silent || !isSubsection) {
        if (isSubsection) {
          // Only log at debug level for subsections since fallback will handle it
          console.debug('Subsection not found (will try parent extraction):', title);
        } else {
          console.error('Error in getSectionByTitle:', errorToThrow);
        }
      }
      throw errorToThrow;
    }
  },

  getOrCreateSection: async (title, projectId, documentType = 'PROTOCOL') => {
    try {
      // Clean and normalize the title once at the beginning
      const cleanTitle = title.trim()
        .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
        .replace(/[^\w\s.-]/g, '') // Remove special characters except dots and dashes
        .toLowerCase();

      // First try to get the section with the cleaned title
      const existingSection = await documentEditorService.getSectionByTitle(cleanTitle, projectId, documentType);
      if (existingSection) {
        return existingSection;
      }

      // If not found, create a new section using the cleaned title
      const newSection = {
        title: cleanTitle,
        context: '',
        output: '',
        prompt: '',
        projectId,
        documentType
      };
      
      const createdSection = await documentEditorService.createSection(newSection, projectId);
      return createdSection.document;
    } catch (error) {
      console.error('Error in getOrCreateSection:', error);
      throw error;
    }
  },

  // ICF Section Service Methods
  createICFSection: async (sectionData, projectId) => {
    try {
      console.log('Creating ICF section with data:', JSON.stringify(sectionData, null, 2));
      console.log('Request URL:', `${API_URL}/icf/create`);
      
      const response = await axios.post(`${API_URL}/icf/create`, { ...sectionData, projectId }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Received response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating ICF section:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // If it's a duplicate section error, handle it gracefully
      if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
        console.log('Section already exists, skipping...');
        return null;
      }
      
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to create section');
      }
      throw error;
    }
  },

  getAllICFSections: async (projectId, options = {}) => {
    try {
      const params = { projectId };
      if (Object.prototype.hasOwnProperty.call(options, 'country')) {
        params.country = options.country;
      }
      if (Object.prototype.hasOwnProperty.call(options, 'site')) {
        params.site = options.site;
      }
      if (options.includeAll) {
        params.includeAll = 'true';
      }
      const response = await axios.get(`${API_URL}/icf/all`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getICFSection: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/icf/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  validateAndCorrectICF: async (projectId, options = {}) => {
    try {
      const payload = { projectId };
      if (Object.prototype.hasOwnProperty.call(options, 'country')) {
        payload.country = options.country;
      }
      if (Object.prototype.hasOwnProperty.call(options, 'site')) {
        payload.site = options.site;
      }
      const response = await axios.post(`${API_URL}/icf/validate-and-correct`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateICFSection: async (sectionId, updateData, projectId) => {
    try {
      const response = await axios.put(`${API_URL}/icf/update/${sectionId}`, { ...updateData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteICFSection: async (sectionId, projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/icf/delete/${sectionId}`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAllICFSections: async (projectId, options = {}) => {
    try {
      const payload = { projectId };
      if (Object.prototype.hasOwnProperty.call(options, 'country')) {
        payload.country = options.country;
      }
      if (Object.prototype.hasOwnProperty.call(options, 'site')) {
        payload.site = options.site;
      }
      const response = await axios.delete(`${API_URL}/icf/deleteAll`, { data: payload });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  addNewICFVersion: async (versionData, projectId) => {
    try {
      const response = await axios.post(`${API_URL}/icf/createVersion/${versionData.sectionId}`, { ...versionData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllICFVersions: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/icf/allVersions/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getICFCountrySummaries: async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/icf/countries`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createICFCountryFromBase: async (projectId, country, options = {}) => {
    try {
      const payload = { projectId, country };
      if (options.sourceCountry) {
        payload.sourceCountry = options.sourceCountry;
      }
      if (options.sourceVersionLabel) {
        payload.sourceVersionLabel = options.sourceVersionLabel;
      }
      const response = await axios.post(`${API_URL}/icf/countries/create`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getICFSiteSummaries: async (projectId, options = {}) => {
    try {
      const params = { projectId };
      if (Object.prototype.hasOwnProperty.call(options, 'country')) {
        params.country = options.country;
      }
      const response = await axios.get(`${API_URL}/icf/sites`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createICFSiteFromCountry: async (projectId, payload) => {
    try {
      const response = await axios.post(`${API_URL}/icf/sites/create`, {
        projectId,
        ...payload,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  translateICFSection: async ({ sectionId, projectId, targetLanguage, baseCountry = null, version = null, country = null, site = null }) => {
    try {
      const response = await axios.post(`${API_URL}/icf/translate/${sectionId}`, {
        projectId,
        targetLanguage,
        baseCountry,
        version,
        country,
        site,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getICFSectionTranslations: async (sectionId, projectId, options = {}) => {
    try {
      const params = { projectId };
      if (options.language) params.language = options.language;
      if (options.version) params.version = options.version;
      if (options.country) params.country = options.country;
      if (options.site) params.site = options.site;
      
      const response = await axios.get(`${API_URL}/icf/translations/${sectionId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  calculateTranslationAccuracy: async (projectId, originalText, retranslatedText) => {
    try {
      const response = await axios.post(`${API_URL}/icf/accuracy`, {
        projectId,
        originalText,
        retranslatedText
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  saveTranslationAccuracy: async (projectId, sectionId, language, version, country, site, accuracyData) => {
    try {
      const response = await axios.post(`${API_URL}/icf/accuracy/save`, {
        projectId,
        sectionId,
        language,
        version: version || 'latest',
        country: country || null,
        site: site || null,
        accuracy: accuracyData.accuracy,
        explanation: accuracyData.explanation || '',
        keyDifferences: accuracyData.keyDifferences || [],
        retranslatedText: accuracyData.retranslatedText || '',
        method: accuracyData.method || 'llm' // Include method for storing both LLM and rule-based accuracies
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateTranslation: async (projectId, sectionId, language, version, country, site, translation) => {
    try {
      const response = await axios.put(`${API_URL}/icf/translation/update`, {
        projectId,
        sectionId,
        language,
        version,
        country,
        site,
        translation,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  retranslateToEnglish: async (projectId, sourceText, sourceLanguage) => {
    try {
      const response = await axios.post(`${API_URL}/icf/retranslate`, {
        projectId,
        sourceText,
        sourceLanguage,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ICF Language Management
  getICFLanguages: async (projectId, options = {}) => {
    try {
      const params = { projectId };
      if (options.country) params.country = options.country;
      if (options.site) params.site = options.site;
      const response = await axios.get(`${API_URL}/icf/languages/all`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createICFLanguage: async (projectId, languageData) => {
    try {
      const response = await axios.post(`${API_URL}/icf/languages/create`, {
        projectId,
        ...languageData,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  approveICFLanguage: async (languageId, projectId) => {
    try {
      const response = await axios.post(`${API_URL}/icf/languages/${languageId}/approve`, {
        projectId,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteICFLanguage: async (languageId, projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/icf/languages/${languageId}`, {
        params: { projectId },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // SAP Section Service Methods
  createSAPSection: async (sectionData, projectId) => {
    try {
      console.log('Creating SAP section with data:', JSON.stringify(sectionData, null, 2));
      console.log('Request URL:', `${API_URL}/sap/create`);
      
      const response = await axios.post(`${API_URL}/sap/create`, { ...sectionData, projectId }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Received response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating SAP section:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // If it's a duplicate section error, handle it gracefully
      if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
        console.log('Section already exists, skipping...');
        return null;
      }
      
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to create section');
      }
      throw error;
    }
  },

  getAllSAPSections: async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap/all`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getSAPSection: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateSAPSection: async (sectionId, updateData, projectId) => {
    try {
      const response = await axios.put(`${API_URL}/sap/update/${sectionId}`, { ...updateData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteSAPSection: async (sectionId, projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/sap/delete/${sectionId}`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAllSAPSections: async (projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/sap/deleteAll`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  addNewSAPVersion: async (versionData, projectId) => {
    try {
      const response = await axios.post(`${API_URL}/sap/createVersion/${versionData.sectionId}`, { ...versionData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllSAPVersions: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap/allVersions/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // SAP-USDM Section Service Methods (separate collection/endpoint from the
  // standard SAP editor's sections above)
  createSAPUsdmSection: async (sectionData, projectId) => {
    try {
      const response = await axios.post(`${API_URL}/sap-usdm/create`, { ...sectionData, projectId }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      // If it's a duplicate section error, handle it gracefully
      if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
        console.log('SAP-USDM section already exists, skipping...');
        return null;
      }
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to create section');
      }
      throw error;
    }
  },

  getSapUsdmTitlePage: async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap-usdm/title-page/${projectId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateSapUsdmTitlePage: async (projectId, updateData) => {
    try {
      const response = await axios.put(`${API_URL}/sap-usdm/title-page/${projectId}`, updateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllSAPUsdmSections: async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap-usdm/all`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getSAPUsdmSection: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap-usdm/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateSAPUsdmSection: async (sectionId, updateData, projectId) => {
    try {
      const response = await axios.put(`${API_URL}/sap-usdm/update/${sectionId}`, { ...updateData, projectId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteSAPUsdmSection: async (sectionId, projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/sap-usdm/delete/${sectionId}`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteAllSAPUsdmSections: async (projectId) => {
    try {
      const response = await axios.delete(`${API_URL}/sap-usdm/deleteAll`, { data: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllSAPUsdmVersions: async (sectionId, projectId) => {
    try {
      const response = await axios.get(`${API_URL}/sap-usdm/allVersions/${sectionId}`, { params: { projectId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export Protocol as PDF via server
  exportProtocolPdf: async (sections) => {
    const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/export/protocol-pdf';
    const response = await axios.post(API, { sections }, { responseType: 'blob' });
    return response.data; // Blob
  },

  convertDocxToPdf: async (docxBlob) => {
    const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/export/docx-to-pdf';
    const form = new FormData();
    form.append('file', docxBlob, 'protocol.docx');
    const response = await axios.post(API, form, { responseType: 'blob', headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data; // Blob
  },

  // HTML->PDF removed (not required)
};

export default documentEditorService; 